import { useSessionStore } from "@auth/sessionStore";
import { PatientClientName } from "@components/PatientClientName";
import {
  faBell,
  faChevronDown,
  faCircleCheck,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRoomsList } from "@rooms/roomsQueries";
import { Button, Checkbox } from "@scribenote/brian-design-system";
import {
  type CardSeverity,
  isUrgent,
  SeverityBadge,
} from "@subjects/cardSeverity";
import { isSameSelection, type PaneSelection } from "@subjects/paneSelection";
import type { SubjectKind } from "@subjects/subjectsClient";
import type { SubjectIndex } from "@subjects/useSubjectIndex";
import { TaskAssigneeBadge } from "@tasks/TaskAssigneeBadge";
import {
  HUMAN,
  identifiedResolver,
  resolverDisplayName,
  type Task,
} from "@tasks/tasksClient";
import { useCompletedTasks } from "@tasks/tasksQueries";
import { formatDateTime, formatPastDateTime } from "@utils/format-date";
import { viewCaption, withSubjectFor } from "@views/viewIdentity";
import { ViewLifecycleBadge } from "@views/ViewLifecycleBadge";
import type { View } from "@views/viewsClient";
import { useRoomOccupants } from "@workspace/useRoomOccupants";
import { useWorkspaceNavigation } from "@workspace/useWorkspaceNavigation";
import { type ReactNode, useState } from "react";

/**
 * The subject pane's index (WEB-3288): every task and view the subject has, with
 * counts. Clicking a row opens it in the reader. Tasks run most-urgent-first.
 *
 * Views split into two disjoint groups: "Right now" is the surfaced set in the
 * agent's order (BE-1909), "OTHER VIEWS" the rest, collapsed. With nothing
 * surfaced the rest shows as one open "Views" group instead. "Completed" (WEB-3300)
 * holds what "Needs you" no longer lists, and sits last: the rail runs
 * most-urgent-first, and nothing in there can need action.
 *
 * Visually it is the folder's index column: caps eyebrows over a stack of card-stock
 * slips laid on the manila, one hairline rule dividing it from the reader. Every
 * clickable row is paper you can see, so what is clickable never has to be guessed
 * at, and the row you have open is the one ringed in dark ink.
 */
export const SubjectRail = ({
  index,
  subjectId,
  kind,
  roomUuid,
  openVisitId,
  selected,
  onSelect,
}: {
  index: SubjectIndex;
  /** The subject whose completed to-dos the Completed group pages. */
  subjectId: string;
  kind: SubjectKind;
  /** The room whose occupants the peer group lists. */
  roomUuid: string | null;
  /** The open patient's visit, dropped from the peers. */
  openVisitId: string | null;
  selected: PaneSelection | null;
  onSelect: (selection: PaneSelection) => void;
}) => {
  const { tasks, surfaced, other, handleAction } = index;

  // Urgent to-dos start expanded; the rail remounts per subject, so the seed
  // re-runs per patient.
  const [expandedTasks, setExpandedTasks] = useState(
    () =>
      new Set(tasks.filter((task) => isUrgent(task.severity)).map((t) => t.id)),
  );
  const toggleTask = (id: string) =>
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const viewRow = (view: View) => (
    <ViewRow
      key={view.id}
      view={view}
      caption={viewCaption(view, {
        withSubject: withSubjectFor(view, subjectId),
      })}
      selected={isSameSelection(selected, { kind: "view", id: view.id })}
      onSelect={() => onSelect({ kind: "view", id: view.id })}
    />
  );

  return (
    <nav
      aria-label="Contents"
      data-testid="subject-rail"
      // overflow-x-hidden backstop: overflow-y-auto forces overflow-x to
      // compute as auto per spec, which would otherwise let stray horizontal
      // overflow show as a scrollbar here.
      className="flex w-80 shrink-0 flex-col gap-5 overflow-y-auto overflow-x-hidden border-r border-rule px-3 py-5"
    >
      <RoomPeers
        roomUuid={roomUuid}
        openVisitId={openVisitId}
        isRoomSubject={kind === "room"}
      />

      {tasks.length > 0 && (
        <RailGroup title="Needs you" count={tasks.length}>
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              selected={isSameSelection(selected, {
                kind: "task",
                id: task.id,
              })}
              expanded={expandedTasks.has(task.id)}
              onToggle={() => toggleTask(task.id)}
              onSelect={() => onSelect({ kind: "task", id: task.id })}
              handleAction={handleAction}
            />
          ))}
        </RailGroup>
      )}

      {surfaced.length > 0 && (
        <RailGroup title="Right now" count={surfaced.length}>
          {surfaced.map(viewRow)}
        </RailGroup>
      )}

      {other.length > 0 && (
        <RailGroup
          title={surfaced.length > 0 ? "Other views" : "Views"}
          count={other.length}
          collapsible={surfaced.length > 0}
        >
          {other.map(viewRow)}
        </RailGroup>
      )}

      <CompletedTasksGroup
        subjectId={subjectId}
        expandedTasks={expandedTasks}
        onToggleTask={toggleTask}
      />
    </nav>
  );
};

/**
 * Whoever else is in the room: "Also in Exam 2" for a patient, "In room" for a
 * room subject. Rows navigate to that patient rather than selecting into the
 * reader, so they aren't a `PaneSelection`. Nothing when alone or roomless.
 */
const RoomPeers = ({
  roomUuid,
  openVisitId,
  isRoomSubject,
}: {
  roomUuid: string | null;
  openVisitId: string | null;
  isRoomSubject: boolean;
}) => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data: rooms } = useRoomsList(sessionKey, true);
  const { selectVisit } = useWorkspaceNavigation();
  const peers = useRoomOccupants(roomUuid).filter(
    (visit) => visit.id !== openVisitId,
  );

  if (peers.length === 0) return null;

  const roomName = rooms.find((room) => room.id === roomUuid)?.name;

  return (
    <RailGroup
      title={isRoomSubject ? "In room" : `Also in ${roomName ?? "room"}`}
      count={peers.length}
    >
      {peers.map((visit) => (
        <RailRow
          key={visit.id}
          selected={false}
          onSelect={() => selectVisit(visit.id, visit.currentRoomUuid)}
          dataTestId={`rail-peer-${visit.id}`}
        >
          <span className="min-w-0 flex-1 truncate">
            <PatientClientName
              patientName={visit.patientName}
              clientName={visit.clientName}
            />
          </span>
        </RailRow>
      ))}
    </RailGroup>
  );
};

/**
 * A titled group of rail rows, with its size beside the heading. `collapsible`
 * makes the heading a toggle that starts closed; the count shows either way.
 *
 * `partialCount` marks the count as a floor rather than a total, for a paged group
 * whose `count` is only what's loaded so far.
 */
const RailGroup = ({
  title,
  count,
  partialCount = false,
  collapsible = false,
  children,
}: {
  title: string;
  count: number;
  partialCount?: boolean;
  collapsible?: boolean;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  // Derived, not seeded: the surfaced set clearing turns this group's toggle off
  // mid-life, and a latched `open` would leave the rows unreachable.
  const isOpen = !collapsible || open;

  const heading = (
    <>
      <span className="min-w-0 truncate">{title}</span>
      {/* The gap between title and count is the flex gap, never whitespace in
          the markup: the group heading and a row's "Completed <date>" line share
          a prefix, and a space here makes the two indistinguishable by text. */}
      <span className="shrink-0 tabular-nums">
        ({count}
        {/* It reads as "at least": the alternative is a total only a count
            endpoint could give. */}
        {partialCount && "+"})
      </span>
      {collapsible && (
        <FontAwesomeIcon
          icon={faChevronDown}
          aria-hidden="true"
          className={`ml-auto shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      )}
    </>
  );

  return (
    <section className="flex flex-col gap-2">
      <h3 className="px-2 text-annotation-caps text-ink">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={isOpen}
            // The eyebrow style again, not just on the h3: a button resets its
            // own font, so a collapsible title would render as body copy in
            // sentence case.
            className="flex w-full cursor-pointer items-center gap-1.5 text-annotation-caps transition-opacity hover:opacity-80 focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent"
          >
            {heading}
          </button>
        ) : (
          <span className="flex items-center gap-1.5">{heading}</span>
        )}
      </h3>
      {isOpen && children}
    </section>
  );
};

/**
 * The severity edge as a side of the border box, spelled out per severity: Tailwind
 * only emits classes it finds literally in the source, so `border-l-${ink}` can't be
 * composed from a shorthand class. Calm has no edge.
 */
const SEVERITY_LEFT_EDGE: Record<CardSeverity, string> = {
  calm: "",
  watch: "border-l-wash-flag",
  urgent: "border-l-ink-stopped",
};

/**
 * Open-row ring per severity, owner-picked by eye rather than by the
 * severityReaderChrome inks: watch matches its highlighter wash exactly (the
 * flag ink is AA-dark for text and reads brown at 2px on cream), and the
 * no-severity ring borrows that same warm brown as a color - ink-flag here is
 * the owner's chosen selection brown, not a status claim.
 */
const SEVERITY_OPEN_BORDER: Record<CardSeverity, string> = {
  calm: "border-ink-flag",
  watch: "border-wash-flag",
  urgent: "border-ink-stopped",
};

/**
 * The row box every clickable line in the rail wears: card stock on the folder's
 * manila, so what can be clicked is a piece of paper rather than a line of text. The
 * border box is on every row in every state, so taking a row from resting to open
 * never moves it.
 *
 * `severity` rides that same border box rather than anything drawn inside the row: a
 * row carrying `watch`/`urgent` is flagged down its left edge in status ink, edge to
 * edge like the tab on a pulled file. Opening it runs that ink all the way round, so
 * the open row is ringed in the colour it already had, and dark ink is the ring for a
 * row with no status of its own.
 *
 * Hover stays quiet: the same paper, lifted a little off the manila.
 */
const rowChrome = (selected: boolean, severity: CardSeverity = "calm") => {
  // `border-transparent` sets border-color and `border-l-*` border-left-color, so the
  // resting edge only survives if the side utility wins. Tailwind v4 emits the
  // per-side colour utilities after the shorthand, so it does: the compiled CSS has
  // `.border-l-wash-flag` / `.border-l-ink-stopped` below `.border-transparent`.
  const border = selected
    ? SEVERITY_OPEN_BORDER[severity]
    : `border-transparent ${SEVERITY_LEFT_EDGE[severity]}`;

  return `rounded-paper border-2 bg-card transition duration-150 ease-paper hover:shadow-lift ${border}`;
};

/**
 * One selectable row: a card-stock slip flagged down its left edge in status ink for
 * `severity`, with the marked status word at the end of the row. Selection runs that
 * same ink around all four sides, or dark ink when the row is calm.
 */
const RailRow = ({
  selected,
  severity = "calm",
  onSelect,
  dataTestId,
  children,
}: {
  selected: boolean;
  severity?: CardSeverity;
  onSelect: () => void;
  dataTestId: string;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onSelect}
    aria-current={selected ? "true" : undefined}
    data-testid={dataTestId}
    className={`flex w-full cursor-pointer items-start gap-2 px-2 py-1.5 text-left text-sm focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent ${rowChrome(
      selected,
      severity,
    )} ${selected ? "font-medium text-ink" : "text-ink-body"}`}
  >
    {children}
    <SeverityBadge severity={severity} dataTestId={`${dataTestId}-severity`} />
  </button>
);

/**
 * A view in the index: name, its caption, its sign-off state, and its open-task
 * count (WEB-3247).
 *
 * The caption is what makes the row selectable at all: every note a patient has
 * is titled `Medical Note` (BE-2395), so three visits give three identical rows
 * and `resolveSelection` opens one of them by default.
 */
const ViewRow = ({
  view,
  caption,
  selected,
  onSelect,
}: {
  view: View;
  caption: string | null;
  selected: boolean;
  onSelect: () => void;
}) => (
  <RailRow
    selected={selected}
    severity={view.severity}
    onSelect={onSelect}
    dataTestId={`rail-view-${view.id}`}
  >
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="truncate">{view.name}</span>
      {caption && (
        <span className="truncate text-fine-print text-annotation">
          {caption}
        </span>
      )}
    </span>
    {/* Checked here too: an empty wrapper would still take a flex gap. */}
    {view.status !== "open" && (
      <span className="shrink-0">
        <ViewLifecycleBadge status={view.status} />
      </span>
    )}
    {view.openTaskCount > 0 && (
      <span
        className="mt-0.5 flex shrink-0 items-center gap-0.5 font-mono text-[0.625rem] text-annotation"
        data-testid={`rail-view-tasks-${view.id}`}
      >
        <FontAwesomeIcon icon={faBell} aria-hidden="true" />
        {view.openTaskCount}
        <span className="sr-only">open tasks</span>
      </span>
    )}
  </RailRow>
);

/**
 * A to-do as an accordion: checkbox, title, chevron; expanded adds reason, due,
 * assignee and Dismiss. Done resolves from the collapsed row so a list can be
 * cleared without opening anything, while Dismiss costs an expand — both are
 * clinic-wide and `PATCH /tasks` has no reopen. A task carrying an a2ui form has
 * nothing to tick, so its row opens in the reader.
 */
const TaskRow = ({
  task,
  selected,
  expanded,
  onToggle,
  onSelect,
  handleAction,
}: {
  task: Task;
  selected: boolean;
  expanded: boolean;
  onToggle: () => void;
  onSelect: () => void;
  handleAction: SubjectIndex["handleAction"];
}) => {
  if (task.kind !== HUMAN)
    return (
      <RailRow
        selected={selected}
        severity={task.severity}
        onSelect={onSelect}
        dataTestId={`rail-task-${task.id}`}
      >
        <span className="w-5 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 line-clamp-2">{task.title}</span>
      </RailRow>
    );

  return (
    <TaskAccordion
      task={task}
      expanded={expanded}
      onToggle={onToggle}
      leading={
        <Checkbox
          checked={false}
          onChange={() => handleAction(task, "complete", {})}
          aria-label={`Mark done: ${task.title}`}
          data-testid={`task-complete-${task.id}`}
        />
      }
    >
      {task.reason && <p className="text-xs text-annotation">{task.reason}</p>}
      {task.dueAt && (
        <time className="text-xs text-annotation">
          Due {formatDateTime(task.dueAt)}
        </time>
      )}
      <TaskAssigneeBadge task={task} />
      <Button
        variant="outline"
        size="sm"
        className="self-start"
        data-testid={`task-dismiss-${task.id}`}
        onClick={() => handleAction(task, "dismiss", {})}
      >
        Dismiss
      </Button>
    </TaskAccordion>
  );
};

/**
 * A to-do row's shell: `leading` control, title, chevron, `children` as the panel.
 * Shared so an outstanding row and a completed one can't drift apart.
 *
 * `resolved` drops the severity chrome — done work shouldn't still read as
 * urgent — but keeps the title in full-strength ink, since a finished to-do is
 * still a record someone reads.
 *
 * Expanded is this row's version of open, so it rings itself the way a selected row
 * does: in its own status ink, or dark ink when it carries none.
 */
const TaskAccordion = ({
  task,
  resolved = false,
  expanded,
  onToggle,
  leading,
  children,
}: {
  task: Task;
  resolved?: boolean;
  expanded: boolean;
  onToggle: () => void;
  leading: ReactNode;
  children: ReactNode;
}) => {
  // A resolved row reads as calm: the edge and badge drop the urgency the task
  // carried while it was open. The title is the row's label either way.
  const severity = resolved ? "calm" : task.severity;

  return (
    <div
      data-testid={`rail-task-${task.id}`}
      className={rowChrome(expanded, severity)}
    >
      {/* Siblings, not nested: an input inside a button is invalid. */}
      <div className="flex items-start gap-2 px-2 py-1.5">
        {/* `h-5` is the title's `text-sm` line box: centres any glyph on the first
            line, and holds it there when the title wraps. A margin wouldn't. */}
        <span className="flex h-5 shrink-0 items-center">{leading}</span>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          data-testid={`rail-task-toggle-${task.id}`}
          className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 text-left focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent"
        >
          <span
            className={`min-w-0 flex-1 text-sm ${
              resolved ? "text-ink" : "text-ink-body"
            } ${expanded ? "" : "line-clamp-1"}`}
          >
            {task.title}
          </span>
          <SeverityBadge
            severity={severity}
            dataTestId={`rail-task-severity-${task.id}`}
          />
          <span className="flex h-5 shrink-0 items-center">
            <FontAwesomeIcon
              icon={faChevronDown}
              aria-hidden="true"
              className={`text-xs text-annotation transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
            />
          </span>
        </button>
      </div>

      {expanded && (
        // Indented to the title's column.
        <div className="flex flex-col gap-2 pb-2 pl-9 pr-2">{children}</div>
      )}
    </div>
  );
};

/**
 * The subject's finished to-dos (WEB-3300), collapsed by default, hidden while the
 * first page is in flight and when there are none. Rows are history only: nothing
 * opens in the reader, since a closed task has no form left and `PATCH /tasks` 409s
 * on a terminal one.
 */
const CompletedTasksGroup = ({
  subjectId,
  expandedTasks,
  onToggleTask,
}: {
  subjectId: string;
  expandedTasks: Set<string>;
  onToggleTask: (id: string) => void;
}) => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useCompletedTasks(sessionKey, subjectId);

  const tasks = data?.pages.flatMap((page) => page.tasks) ?? [];

  // A failed read must not read as "this patient has finished nothing" — that's the
  // one thing staff check the group for before raising a to-do again. Only once the
  // retries settle, and only with nothing loaded: a failed later page keeps its rows.
  if (isError && tasks.length === 0)
    return (
      <RailGroup title="Completed" count={0} collapsible>
        <p className="px-2 text-xs text-annotation">
          Couldn&apos;t load history
        </p>
      </RailGroup>
    );

  if (tasks.length === 0) return null;

  return (
    <RailGroup
      title="Completed"
      count={tasks.length}
      partialCount={hasNextPage}
      collapsible
    >
      {tasks.map((task) => (
        <CompletedTaskRow
          key={task.id}
          task={task}
          expanded={expandedTasks.has(task.id)}
          onToggle={() => onToggleTask(task.id)}
        />
      ))}
      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          data-testid="rail-completed-load-more"
          disabled={isFetchingNextPage}
          onClick={() => fetchNextPage()}
        >
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      )}
    </RailGroup>
  );
};

/**
 * A finished to-do: green check for the tick box, panel says why, who and when.
 * "Who" is the resolver (BE-2276), not the assignee — Brian closing its own
 * task must not read as the assigned person's work; Brian-blue marks an agent
 * close, matching replay's "Brian did this" ink. Rows with no identified
 * resolver — closed before the backend recorded attribution, or attributed
 * only to a bare kind (see `identifiedResolver`) — keep the old display,
 * time plus assignee badge, and `closedAt` is null pre-BE-2200.
 */
const CompletedTaskRow = ({
  task,
  expanded,
  onToggle,
}: {
  task: Task;
  expanded: boolean;
  onToggle: () => void;
}) => {
  const resolver = identifiedResolver(task);
  return (
    <TaskAccordion
      task={task}
      resolved
      expanded={expanded}
      onToggle={onToggle}
      leading={
        <span className="text-xs text-ink-working">
          <FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" />
          <span className="sr-only">Completed</span>
        </span>
      }
    >
      {task.reason && <p className="text-xs text-annotation">{task.reason}</p>}
      {resolver ? (
        <p
          className="text-xs text-annotation"
          data-testid={`task-resolved-by-${task.id}`}
        >
          Completed by{" "}
          <span
            className={
              resolver.actorKind === "agent"
                ? "font-medium text-ink-brian"
                : undefined
            }
          >
            {resolverDisplayName(resolver)}
          </span>
          {task.closedAt && (
            <>
              {" "}
              · <time>{formatPastDateTime(task.closedAt)}</time>
            </>
          )}
        </p>
      ) : (
        <>
          {task.closedAt && (
            <time className="text-xs text-annotation">
              Completed {formatPastDateTime(task.closedAt)}
            </time>
          )}
          <TaskAssigneeBadge task={task} />
        </>
      )}
    </TaskAccordion>
  );
};
